<template>
  <section class="coupon-setting" :v-loading="loading">
    <section class="section-block">
      <SectionTitle :title="$t('businessTimeSettings.branchBusinessTime.title')" @edit="showDialog = true" />
      <BaseElForm label-position="left" label-width="250px">
        <BaseElFormItem :label="$t('businessTimeSettings.branchBusinessTime.title')" class="settings-item">
          <BaseElForm v-if="showControl.customWeek" label-position="left" label-width="79px">
            <BaseElFormItem v-for="item in weeksOptions" :key="item.value" :label="$t(item.label)" class="week-label">
              <p v-if="formatTimeRange(item.value).length == 0">{{ $t('businessTimeSettings.dayoff.title') }}</p>
              <template v-else>
                <p v-for="(item,index) in formatTimeRange(item.value)" :key="index">{{ item.timeStart }} ~ {{ item.timeEnd }} </p>
              </template>
            </BaseElFormItem>
          </BaseElForm>
          <div v-else class="text-[#5E5E5E] text-[14px]">{{ $t('businessTimeSettings.24hours.title') }}</div>
        </BaseElFormItem>
        <BaseElFormItem :label="displayText.excludedDays" class="settings-item">
          <span data-cy="excluded-day" class="content">{{ formData.excludedDays ? $t('common.open.text') : $t('common.close.text') }}</span>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.shopExcludeTimeRules" :label="displayText.shopExcludeTimeRules" class="settings-item">
          <span class="content grid gap-[12px]">
            <p v-for="(item, index) in renderExcludeTime(formData.shopExcludeTimeRules)" :key="index">
              {{ item }}
            </p>
          </span>
        </BaseElFormItem>
      </BaseElForm>
    </section>
    <ShopBusinessTimeDialog
      v-if="showDialog"
      :visible="showDialog"
      :formData="formData"
      :formRules="formRules"
      @close="showDialog = false"
      @confirm="confirm"
    />
  </section>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue'
import ShopBusinessTimeDialog from './ShopBusinessTimeDialog.vue'
import { weeksOptions, businessTimeOptions } from '@/config/date'
import { noEmptyWeekRangeInterval, noEmptyRules } from '@/validation'
import { isArray, get, isEmpty } from 'lodash'
import store from '@/store'
import { GetShopBusinessTimeConfig, UpdateShopBusinessTimeConfig } from '@/api/shopBusinessTime'
import { i18n } from '@/plugins/i18n/i18n'
import { useBusinessTime } from '@/use/useBusinessTime'

export default defineComponent({
  name: 'ShopBusinessTimeSettings',
  components: { ShopBusinessTimeDialog },
  setup () {
    const {
      mergeTimeIntervals,
      timeToMinutes,
      minutesToTime,
      renderExcludeTime,
      formatExcludeTime,
      syncShopExcludeTimeRules,
    } = useBusinessTime()
    const loading = ref(false)
    const formData = reactive({
      mode: null,
      customWeekConfigs: [],
      excludedDays: false,
      allExcludeTimeRules: [],
      shopExcludeTimeRules: [],
    })
    const formBusinessRef = ref(null)
    const formRules = computed(() => {
      const rules = {
        customWeekConfigs: [noEmptyWeekRangeInterval()],
        shopExcludeTimeRules: [noEmptyRules()],
      }
      return rules
    })
    const showDialog = ref(false)
    const shopId = computed(() => store.getters.shop)
    const displayText = computed(() => {
      const data = {
        excludedDays: '分店排除特定營業時間',
        shopExcludeTimeRules: '分店排除特定時段',
      }
      return data
    })
    const showControl = computed(() => {
      const controls = {
        customWeek: formData.mode === 'CustomWeek',
        shopExcludeTimeRules: formData.excludedDays,
      }
      return controls
    })
    const confirm = async (data) => {
      await updateShopBusinessTimeConfig(data)
      showDialog.value = false
    }
    const syncData = async (data) => {
      formData.mode = get(data, 'mode')

      const customWeekConfigs = get(data, 'customWeekConfigs')
      const newWeeks = customWeekConfigs.map(item => {
        const { week, timeStart, timeEnd } = item
        return {
          week: week,
          timeStart: minutesToTime(timeStart),
          timeEnd: minutesToTime(timeEnd),
        }
      })
      formData.customWeekConfigs = newWeeks || [{ week: null, timeStart: null, timeEnd: null }]
      formData.shopExcludeTimeRules = syncShopExcludeTimeRules(get(data, 'shopExcludeTimeRules'))
      formData.excludedDays = !isEmpty(get(data, 'shopExcludeTimeRules'))
    }
    const getShopBusinessTimeConfig = async () => {
      loading.value = true
      const [res, err] = await GetShopBusinessTimeConfig({
        shopId: shopId.value,
      })
      if (err) {
        window.$message.error(err)
        return
      }
      await syncData(res)
    }
    const updateShopBusinessTimeConfig = async (data) => {
      loading.value = true
      const { customWeekConfigs, mode, shopExcludeTimeRules, excludedDays } = data
      const newWeeks = []
      customWeekConfigs.forEach(item => {
        const { week, timeStart, timeEnd } = item
        newWeeks.push({
          week: week,
          timeStart: timeToMinutes(timeStart),
          timeEnd: timeToMinutes(timeEnd),
        })
      })
      const [, err] = await UpdateShopBusinessTimeConfig({
        shopId: shopId.value,
        mode,
        customWeekConfigs: mergeTimeIntervals(newWeeks),
        shopExcludeTimeRules: excludedDays ? formatExcludeTime(shopExcludeTimeRules) : null,
      })
      if (err) {
        window.$message.error(err)
        return
      }
      window.$message.success(i18n.t('common.message.updateSuccess'))
      getShopBusinessTimeConfig()
    }

    const formatTimeRange = (value) => {
      return formData.customWeekConfigs.filter(item => item.week === value).sort()
    }

    onMounted(() => {
      getShopBusinessTimeConfig()
    })
    return {
      loading,
      formData,
      showDialog,
      confirm,
      weeksOptions,
      businessTimeOptions,
      isArray,
      formatTimeRange,
      formRules,
      formBusinessRef,
      renderExcludeTime,
      displayText,
      showControl,
    }
  },
})
</script>

<style lang="postcss" scoped>
::v-deep .el-select .el-input {
  @apply w-auto
}
ol {
  list-style: demical;
  padding-left: 16px;
}
::v-deep .el-form-item__label, ::v-deep .el-form-item__content {
  color: var(--info);
  line-height: 20.27px;
}

::v-deep .el-radio {
  display: inline-flex;
  align-items: center;
}

::v-deep .el-radio__inner {
  width: 20px;
  height: 20px;
}

::v-deep .el-radio-group .el-radio__inner::after {
  width: 15px;
  height: 15px;
  border-width: 3px;
}

.week-label {
  @apply mb-[22px]
}
</style>
